/**
 * User-icon dropdown for the runtime top bar. Shows the signed-in identity and
 * opens the self-service account panel; admins additionally get a "System
 * administration" entry. Surface-agnostic (driven by ShellIdentity + ShellApi).
 *
 * It is also the ONE place an end user picks how the application looks:
 * Appearance (Light / Dark) and — inside the Run shell — the Layout.
 * Theme-aware (`--color-*` with light fallbacks) and keyboard-operable
 * (WAI-ARIA menu: arrows move, Esc closes and returns focus).
 */
import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { ChevronDown, ChevronRight, LayoutTemplate, LogOut, Moon, Settings, ShieldCheck, Sun, UserCircle2 } from 'lucide-react'
import AccountModal from './AccountModal'
import AdminConsole, { type AdminExtraTab } from './AdminConsole'
import type { ShellApi, ShellCapabilities, ShellIdentity, ShellNotify } from './types'
import type { LayoutDescriptor, LayoutId } from './nav/config'
import { LayoutPickerDialog } from './nav/parts/LayoutPicker'

/** An extra entry injected into the user menu by a host surface (e.g. the
 *  running app's "Project settings"). */
export interface UserMenuItem {
  label: string
  onClick: () => void
  icon?: ReactNode
  /** A count shown at the entry's end (e.g. support issues waiting on you). */
  badge?: number
  /** Hover text. */
  title?: string
}

/** The look choices the menu offers (each absent ⇒ not offered). */
export interface UserMenuLook {
  appearance?: { value: 'light' | 'dark'; onChange: (v: 'light' | 'dark') => void }
  layout?: { value: LayoutId; options: LayoutDescriptor[]; onChange: (id: LayoutId) => void }
  /** Asked before signing out (e.g. unsaved open editors). False = stay. */
  beforeSignOut?: () => Promise<boolean> | boolean
  /** After the user confirmed signing out. */
  afterSignOut?: () => void
}

const T = {
  panel: 'var(--color-raised, var(--color-bg-secondary, #ffffff))',
  border: 'var(--color-border, #e2e8f0)',
  text: 'var(--color-text, #0f172a)',
  text2: 'var(--color-text-secondary, #475569)',
  faint: 'var(--color-text-faint, #687281)',
  hover: 'var(--color-surface-2, #f1f5f9)',
  seg: 'var(--color-bg, #eef1f5)',
  danger: 'var(--color-error, #dc2626)',
}

function CountBadge({ n, accent }: { n: number; accent: string }) {
  return (
    <span style={{
      marginLeft: 'auto', minWidth: 18, height: 18, padding: '0 5px',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1, borderRadius: 9, background: accent, color: '#fff',
    }}>
      {n > 99 ? '99+' : n}
    </span>
  )
}

const MENU_CSS = `
.tw-um-item:hover,.tw-um-item:focus-visible{background:${T.hover}!important;outline:none}
.tw-um-btn:focus-visible{outline:2px solid var(--color-accent,#2563eb);outline-offset:2px}
`

export default function UserMenu({
  identity, api, caps, accentColor = '#4f46e5', extraItems, adminExtraTabs, notify, look,
}: {
  identity: ShellIdentity
  api: ShellApi
  caps: ShellCapabilities
  accentColor?: string
  extraItems?: UserMenuItem[]
  adminExtraTabs?: AdminExtraTab[]
  /** Host toast adapter, threaded into the account/admin panels. */
  notify?: ShellNotify
  /** Appearance / Layout / sign-out guard. */
  look?: UserMenuLook
}) {
  const [open, setOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  const [layoutOpen, setLayoutOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Only offer the in-shell "System administration" console when the surface
  // actually exposes something to administer. A host that manages users/data
  // on its own pages (the standalone runtime's /rbac, /admin/database) passes
  // caps {users:false, access:false} and injects its links via `extraItems`.
  const showAdminConsole = identity.isAdmin
    && (caps.users || caps.access || !!caps.dataAdminUrl || (adminExtraTabs?.length ?? 0) > 0)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDown)
    // Focus the first entry, as a menu should.
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"],[role="menuitemradio"]')?.focus()
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const initial = (identity.username || '?').slice(0, 1).toUpperCase()
  // Something in the menu is waiting on this user (an entry's badge): say so
  // on the closed avatar too, or it is only found by opening the menu.
  const waiting = (extraItems || []).reduce((n, it) => n + (it.badge && it.badge > 0 ? it.badge : 0), 0)
  const appRoles = identity.roles.filter((r) => r !== 'admin')

  const close = (refocus = true) => { setOpen(false); if (refocus) btnRef.current?.focus() }
  const doLogout = async () => {
    setOpen(false)
    try {
      if (look?.beforeSignOut && !(await look.beforeSignOut())) return
      look?.afterSignOut?.()
    } catch (err) {
      console.warn('[user menu] sign-out check failed', err)
    }
    try { await api.logout() } catch { /* idempotent */ }
  }

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"],[role="menuitemradio"]') || [])]
    const i = items.indexOf(document.activeElement as HTMLElement)
    const go = (j: number) => { e.preventDefault(); items[(j + items.length) % items.length]?.focus() }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close() }
    else if (e.key === 'ArrowDown') go(i + 1)
    else if (e.key === 'ArrowUp') go(i - 1)
    else if (e.key === 'Home') go(0)
    else if (e.key === 'End') go(items.length - 1)
    else if (e.key === 'Tab') setOpen(false)
  }

  const item: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
    minHeight: 40, padding: '0.5rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '0.9375rem', color: T.text, fontFamily: 'inherit',
  }
  const sectionLabel: React.CSSProperties = {
    padding: '0.5rem 0.85rem 0.25rem', fontSize: '0.8125rem', fontWeight: 600, color: T.text2,
  }
  const layoutLabel = look?.layout?.options.find((o) => o.id === look.layout?.value)?.label

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <style>{MENU_CSS}</style>
      <button
        ref={btnRef}
        type="button" onClick={() => setOpen((v) => !v)}
        title={waiting ? `${identity.username} — ${waiting} waiting on you` : identity.username}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        className="tw-um-btn"
        data-tw-action="user-menu"
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true) } }}
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', gap: 8, padding: '0.25rem 0.4rem 0.25rem 0.3rem',
          background: 'none', border: `1px solid ${T.border}`, borderRadius: 999, cursor: 'pointer',
          color: T.text2,
        }}
      >
        <span style={{
          width: 30, height: 30, borderRadius: '50%', background: accentColor, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9125rem',
        }}>{initial}</span>
        <ChevronDown size={15} />
        {waiting > 0 && (
          <span aria-label={`${waiting} waiting`} style={{
            position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, padding: '0 4px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1, borderRadius: 9,
            background: 'var(--color-accent, #4c8dff)', color: '#fff', border: `2px solid ${T.panel}`,
          }}>
            {waiting > 99 ? '99+' : waiting}
          </span>
        )}
      </button>

      {open && (
        <div ref={menuRef} role="menu" aria-label="Account" onKeyDown={onMenuKey} style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 264, zIndex: 200,
          background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12,
          boxShadow: '0 12px 32px rgba(0,0,0,0.22)', overflow: 'hidden', color: T.text, padding: '0 0 4px',
        }}>
          <div style={{ padding: '0.85rem', borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 10, alignItems: 'center' }}>
            <UserCircle2 size={32} color={accentColor} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '0.9625rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{identity.username}</div>
              <div style={{ fontSize: '0.8125rem', color: T.text2 }}>
                {identity.isAdmin ? 'Administrator' : (appRoles.length ? appRoles.join(', ') : 'Standard user')}
              </div>
            </div>
          </div>

          {look?.appearance && (
            <>
              <div style={sectionLabel} id="tw-um-appearance">Appearance</div>
              <div role="group" aria-labelledby="tw-um-appearance" style={{
                display: 'flex', gap: 4, margin: '0 0.6rem 0.4rem', padding: 3, borderRadius: 9, background: T.seg,
              }}>
                {(['light', 'dark'] as const).map((v) => {
                  const on = look.appearance?.value === v
                  return (
                    <button key={v} type="button" role="menuitemradio" aria-checked={on}
                      className="tw-um-item" data-tw-action={`appearance-${v}`}
                      onClick={() => look.appearance?.onChange(v)}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        height: 34, border: 'none', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
                        background: on ? T.panel : 'transparent', color: T.text, fontSize: '0.875rem',
                        fontWeight: on ? 600 : 500, boxShadow: on ? '0 1px 2px rgba(0,0,0,0.14)' : 'none',
                      }}>
                      {v === 'light' ? <Sun size={15} /> : <Moon size={15} />}
                      {v === 'light' ? 'Light' : 'Dark'}
                    </button>
                  )
                })}
              </div>
            </>
          )}
          {look?.layout && (
            <button type="button" role="menuitem" className="tw-um-item" style={item}
              aria-haspopup="dialog" data-tw-action="shell-layout-menu"
              onClick={() => { setOpen(false); setLayoutOpen(true) }}>
              <LayoutTemplate size={16} /> Layout
              <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, color: T.text2, fontSize: '0.875rem' }}>
                {layoutLabel}<ChevronRight size={14} />
              </span>
            </button>
          )}
          {(look?.appearance || look?.layout) && <div style={{ borderTop: `1px solid ${T.border}`, margin: '4px 0' }} />}

          <button type="button" role="menuitem" className="tw-um-item" style={item}
            onClick={() => { setOpen(false); setAccountOpen(true) }}>
            <Settings size={16} /> My account
          </button>

          {(extraItems || []).map((it, i) => (
            <button key={i} type="button" role="menuitem" className="tw-um-item" style={item} title={it.title}
              onClick={() => { setOpen(false); it.onClick() }}>
              {it.icon ?? <Settings size={16} />} {it.label}
              {it.badge && it.badge > 0 ? <CountBadge n={it.badge} accent="var(--color-accent, #4c8dff)" /> : null}
            </button>
          ))}

          {showAdminConsole && (
            <>
              <div style={{ borderTop: `1px solid ${T.border}` }} />
              <div style={{ padding: '0.4rem 0.85rem 0.1rem', fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: T.faint }}>
                System Admin
              </div>
              <button type="button" role="menuitem" className="tw-um-item" style={item}
                onClick={() => { setOpen(false); setAdminOpen(true) }}>
                <ShieldCheck size={16} /> System administration
              </button>
            </>
          )}

          <div style={{ borderTop: `1px solid ${T.border}` }} />
          <button type="button" role="menuitem" className="tw-um-item" style={{ ...item, color: T.danger }}
            data-tw-action="sign-out" onClick={doLogout}>
            <LogOut size={16} /> Sign out
          </button>
        </div>
      )}

      <AccountModal open={accountOpen} onClose={() => setAccountOpen(false)} identity={identity} api={api} notify={notify} />
      {showAdminConsole && (
        <AdminConsole open={adminOpen} onClose={() => setAdminOpen(false)} identity={identity} api={api} caps={caps} extraTabs={adminExtraTabs} notify={notify} />
      )}
      {layoutOpen && look?.layout && (
        <LayoutPickerDialog value={look.layout.value} options={look.layout.options}
          onChange={look.layout.onChange} onClose={() => setLayoutOpen(false)} />
      )}
    </div>
  )
}
